// stores/chatHistoryStore.ts
import { create } from "zustand";
import { registerOrgScopedReset } from "@/lib/org-scope";
import type { ChatSession as BackendChatSession, ChatMessage as BackendChatMessage } from "@/services/chat/chatService";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
  /** Persisted from backend (impact/test/tooling analysis, clarification, etc.). */
  metadata?: Record<string, unknown>;
}

export interface ChatSession {
  id: string;
  title: string;            // shown in sidebar
  threadId: string;         // backend chat session UUID
  backendId?: number;       // backend chat session ID
  messages: ChatMessage[];
  createdAt: string;
  status?: "active" | "completed" | "archived";
}

interface ChatHistoryState {
  sessions: ChatSession[];
  activeSessionId: string | null;
  activeSession: ChatSession | null;
  isLoading: boolean;
  
  // actions
  newSession: (title?: string, backendSession?: BackendChatSession) => void;
  setActiveSession: (id: string) => void;
  addMessageToActive: (role: "user" | "assistant", content: string, backendMessage?: BackendChatMessage) => void;
  updateSessionTitle: (sessionId: string, title: string) => void;
  loadSessionsFromBackend: (backendSessions: BackendChatSession[]) => void;
  loadMessagesForSession: (sessionId: string, backendMessages: BackendChatMessage[]) => void;
  setLoading: (loading: boolean) => void;
  updateSessionFromBackend: (backendSession: BackendChatSession) => void;
  removeSession: (sessionId: string) => void;
}

export const useChatHistoryStore = create<ChatHistoryState>()(
    (set, get) => ({
      sessions: [],
      activeSessionId: null,
      activeSession: null,
      isLoading: false,

      setLoading: (loading) => set({ isLoading: loading }),

      newSession: (title = "New chat", backendSession) => {
        const id = backendSession?.uuid || crypto.randomUUID();
        const session: ChatSession = {
          id,
          title: backendSession?.title || title,
          threadId: backendSession?.uuid || id,
          backendId: backendSession?.id,
          messages: [],
          createdAt: backendSession?.inserted_at || new Date().toISOString(),
          status: backendSession?.status || "active",
        };
        set((state) => ({
          sessions: [session, ...state.sessions],
          activeSessionId: id,
          activeSession: session,
        }));
      },

      setActiveSession: (id) => {
        const { sessions } = get();
        const session = sessions.find((s) => s.id === id) || null;
        set({ activeSessionId: id, activeSession: session });
      },

      addMessageToActive: (role, content, backendMessage) => {
        const { activeSessionId, sessions } = get();
        if (!activeSessionId) return;

        const updatedSessions = sessions.map((s) => {
          if (s.id === activeSessionId) {
            const newMessage: ChatMessage = {
              id: backendMessage?.uuid || crypto.randomUUID(),
              role,
              content,
              timestamp: backendMessage?.inserted_at || new Date().toISOString(),
            };
            const updatedMessages = [...s.messages, newMessage];

            // If this is the first user message and title is still default ("New chat" / "New Chat"), update title
            let updatedTitle = s.title;
            const isDefaultTitle = s.title === "New chat" || s.title === "New Chat";
            if (role === "user" && s.messages.length === 0 && isDefaultTitle) {
              updatedTitle = content.trim().slice(0, 50) || "New chat";
            }

            return {
              ...s,
              title: updatedTitle,
              messages: updatedMessages,
            };
          }
          return s;
        });

        const updatedActiveSession =
          updatedSessions.find((s) => s.id === activeSessionId) || null;

        set({
          sessions: updatedSessions,
          activeSession: updatedActiveSession,
        });
      },

      updateSessionTitle: (sessionId, title) => {
        const { sessions, activeSessionId, activeSession } = get();
        const updatedSessions = sessions.map((s) =>
          s.id === sessionId ? { ...s, title } : s
        );

        set({
          sessions: updatedSessions,
          activeSession:
            activeSessionId === sessionId
              ? updatedSessions.find((s) => s.id === sessionId) || null
              : activeSession,
        });
      },

      loadSessionsFromBackend: (backendSessions) => {
        const { activeSessionId } = get();
        const sessions: ChatSession[] = backendSessions?.map((bs) => ({
          id: bs.uuid,
          title: bs.title || "New chat",
          threadId: bs.uuid,
          backendId: bs.id,
          messages: [],
          createdAt: bs.inserted_at,
          status: bs.status,
        }));

        // Sync activeSession — reset to first session if active was deleted
        const stillExists = activeSessionId
          ? sessions.find((s) => s.id === activeSessionId)
          : null;
        const newActiveSessionId = stillExists ? activeSessionId : (sessions[0]?.id || null);
        const activeSession = newActiveSessionId
          ? sessions.find((s) => s.id === newActiveSessionId) || null
          : null;

        set({ sessions, activeSessionId: newActiveSessionId, activeSession });
      },

      loadMessagesForSession: (sessionId, backendMessages) => {
        const { sessions, activeSessionId } = get();

        const messages: ChatMessage[] = backendMessages.map((bm) => ({
          id: bm.uuid,
          role: bm.role,
          content: bm.content,
          timestamp: bm.inserted_at,
          metadata: bm.metadata,
        }));

        const updatedSessions = sessions.map((s) =>
          s.id === sessionId ? { ...s, messages } : s
        );

        const updatedActiveSession =
          activeSessionId === sessionId
            ? updatedSessions.find((s) => s.id === sessionId) || null
            : get().activeSession;

        set({
          sessions: updatedSessions,
          activeSession: updatedActiveSession,
        });
      },

      updateSessionFromBackend: (backendSession) => {
        const { sessions, activeSessionId } = get();

        const updatedSessions = sessions.map((s) =>
          s.threadId === backendSession.uuid
            ? {
                ...s,
                title: backendSession.title || s.title,
                status: backendSession.status,
                backendId: backendSession.id,
              }
            : s
        );

        const updatedActiveSession =
          activeSessionId === backendSession.uuid
            ? updatedSessions.find((s) => s.threadId === backendSession.uuid) ||
              null
            : get().activeSession;

        set({
          sessions: updatedSessions,
          activeSession: updatedActiveSession,
        });
      },

      removeSession: (sessionId) => {
        const { sessions, activeSessionId } = get();
        const filtered = sessions.filter((s) => s.id !== sessionId);

        // If the removed session was active, switch to the first remaining or null
        const newActiveId = activeSessionId === sessionId
          ? (filtered[0]?.id || null)
          : activeSessionId;
        const newActive = newActiveId ? filtered.find((s) => s.id === newActiveId) || null : null;

        set({
          sessions: filtered,
          activeSessionId: newActiveId,
          activeSession: newActive,
        });
      },
    })
);

// Remove stale localStorage data from the old persist middleware
if (typeof window !== 'undefined') {
  localStorage.removeItem('chat-history-storage');
}

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Chat sessions and their messages are backend records scoped to one
// organization. They are re-fetched per organization, so dropping the local copy
// is both safe and necessary — otherwise the sidebar lists Org A's conversations
// until Org B's fetch resolves.
registerOrgScopedReset("chatHistoryStore", () => {
  useChatHistoryStore.setState({
    sessions: [],
    activeSessionId: null,
    activeSession: null,
    isLoading: false,
  });
});
